import { 
  GameState, 
  Position, 
  Piece, 
  PieceType, 
  Board,
  validateMove,
  MoveResult,
  getPieceAt
} from './chess-engine';

export interface HatchGameState extends GameState {
  evolutionHistory: EvolutionRecord[];
}

export interface EvolutionRecord {
  position: Position;
  originalPiece: PieceType;
  evolvedTo: PieceType;
  moveNumber: number;
  capturedPiece: PieceType;
}

/**
 * Evolution chain mapping - what pieces evolve into after capture
 */
const EVOLUTION_CHAIN: Record<PieceType, PieceType | null> = {
  'P': 'N', // Pawn -> Knight
  'B': 'R', // Bishop -> Rook  
  'R': 'Q', // Rook -> Queen
  'N': null, // Knight doesn't evolve
  'Q': null, // Queen doesn't evolve (terminal)
  'K': null  // King doesn't evolve (terminal)
};

/**
 * Check if a piece can evolve after capturing
 */
export const canPieceEvolve = (pieceType: PieceType): boolean => {
  return EVOLUTION_CHAIN[pieceType] !== null;
};

/**
 * Get the evolved form of a piece
 */
export const getEvolutionTarget = (pieceType: PieceType): PieceType | null => {
  return EVOLUTION_CHAIN[pieceType];
};

/**
 * Create initial hatch game state
 */
export const createInitialHatchGameState = (): HatchGameState => {
  const baseState = {
    board: [
      [
        { type: 'R' as PieceType, color: 'black' as const }, 
        { type: 'N' as PieceType, color: 'black' as const }, 
        { type: 'B' as PieceType, color: 'black' as const }, 
        { type: 'Q' as PieceType, color: 'black' as const },
        { type: 'K' as PieceType, color: 'black' as const }, 
        { type: 'B' as PieceType, color: 'black' as const }, 
        { type: 'N' as PieceType, color: 'black' as const }, 
        { type: 'R' as PieceType, color: 'black' as const }
      ],
      Array(8).fill(null).map(() => ({ type: 'P' as PieceType, color: 'black' as const })),
      Array(8).fill(null),
      Array(8).fill(null),
      Array(8).fill(null),
      Array(8).fill(null),
      Array(8).fill(null).map(() => ({ type: 'P' as PieceType, color: 'white' as const })),
      [
        { type: 'R' as PieceType, color: 'white' as const }, 
        { type: 'N' as PieceType, color: 'white' as const }, 
        { type: 'B' as PieceType, color: 'white' as const }, 
        { type: 'Q' as PieceType, color: 'white' as const },
        { type: 'K' as PieceType, color: 'white' as const }, 
        { type: 'B' as PieceType, color: 'white' as const }, 
        { type: 'N' as PieceType, color: 'white' as const }, 
        { type: 'R' as PieceType, color: 'white' as const }
      ]
    ],
    sideToMove: 'white' as const,
    castlingRights: {
      whiteKingSide: true,
      whiteQueenSide: true,
      blackKingSide: true,
      blackQueenSide: true
    },
    enPassantTarget: null,
    halfmoveClock: 0,
    fullmoveNumber: 1,
    moveHistory: [],
    positionHistory: new Map()
  };

  return {
    ...baseState,
    evolutionHistory: []
  };
};

/**
 * Validate and execute a move with evolution logic
 */
export const validateHatchMove = (
  gameState: HatchGameState,
  from: Position,
  to: Position,
  promotion?: PieceType
): MoveResult & { evolutionRecord?: EvolutionRecord } => {
  // First validate the move using standard chess rules
  const baseResult = validateMove(gameState, from, to, promotion);
  
  if (!baseResult.isLegal || !baseResult.newState) {
    return baseResult;
  }

  const movingPiece = getPieceAt(gameState.board, from);
  const capturedPiece = getPieceAt(gameState.board, to);
  
  // Check if this move involves a capture and the piece can evolve
  if (capturedPiece && movingPiece && canPieceEvolve(movingPiece.type)) {
    const evolvedType = getEvolutionTarget(movingPiece.type);
    
    if (evolvedType) {
      // Create evolution record
      const evolutionRecord: EvolutionRecord = {
        position: to,
        originalPiece: movingPiece.type,
        evolvedTo: evolvedType,
        moveNumber: gameState.fullmoveNumber,
        capturedPiece: capturedPiece.type
      };

      // Apply evolution to the new board state
      const hatchState = baseResult.newState as HatchGameState;
      hatchState.board[to[0]][to[1]] = {
        type: evolvedType,
        color: movingPiece.color
      };

      // Add evolution to history
      hatchState.evolutionHistory = [
        ...(gameState.evolutionHistory || []),
        evolutionRecord
      ];

      console.log(`🧬 Evolution! ${movingPiece.type} captured ${capturedPiece.type} and evolved to ${evolvedType}`);

      return {
        ...baseResult,
        newState: hatchState,
        evolutionRecord
      };
    }
  }

  // No evolution occurred, return standard result but ensure it's a HatchGameState
  const hatchState = baseResult.newState as HatchGameState;
  hatchState.evolutionHistory = gameState.evolutionHistory || [];

  return {
    ...baseResult,
    newState: hatchState
  };
};

/**
 * Get evolution chain description for UI
 */
export const getEvolutionChainDescription = (): string => {
  return "Pawn ➜ Knight ➜ (stays Knight)\nBishop ➜ Rook ➜ Queen\nRook ➜ Queen ➜ (stays Queen)\nKnight, Queen, King don't evolve";
};

/**
 * Check if a piece has evolved from its original type
 */
export const isPieceEvolved = (
  gameState: HatchGameState,
  position: Position
): boolean => {
  return gameState.evolutionHistory.some(
    record => record.position[0] === position[0] && record.position[1] === position[1]
  );
};

/**
 * Get evolution history for a specific position
 */
export const getEvolutionHistory = (
  gameState: HatchGameState,
  position: Position
): EvolutionRecord | null => {
  const evolutions = gameState.evolutionHistory.filter(
    record => record.position[0] === position[0] && record.position[1] === position[1]
  );
  
  // Return the most recent evolution for this position
  return evolutions.length > 0 ? evolutions[evolutions.length - 1] : null;
};